import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Search, Eye, Building2, Ban, ShieldCheck, Settings2, FileCheck, Wallet as WalletIcon,
  MoreVertical, Users, UserPlus, Clock, CheckCircle2,
} from 'lucide-react';

const KYC_BADGE = {
  not_started: 'bg-muted text-muted-foreground border-border',
  incomplete: 'bg-slate-500/15 text-slate-500 border-slate-500/30',
  pending: 'bg-yellow-500/20 text-yellow-600 border-yellow-500/30',
  submitted: 'bg-yellow-500/20 text-yellow-600 border-yellow-500/30',
  under_review: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
  approved: 'bg-green-500/20 text-green-600 border-green-500/30',
  rejected: 'bg-red-500/20 text-red-500 border-red-500/30',
  reupload_required: 'bg-orange-500/20 text-orange-500 border-orange-500/30',
};
const SERVICE_BADGE = {
  active: 'bg-green-500/20 text-green-600 border-green-500/30',
  available: 'bg-green-500/20 text-green-600 border-green-500/30',
  locked: 'bg-yellow-500/20 text-yellow-600 border-yellow-500/30',
  disabled: 'bg-muted text-muted-foreground border-border',
  restricted: 'bg-red-500/20 text-red-500 border-red-500/30',
  inactive: 'bg-muted text-muted-foreground border-border',
  frozen: 'bg-red-500/20 text-red-500 border-red-500/30',
  suspended: 'bg-red-500/20 text-red-500 border-red-500/30',
};
const RISK_BADGE = {
  medium: 'bg-green-500/20 text-green-600 border-green-500/30',
  high: 'bg-orange-500/20 text-orange-500 border-orange-500/30',
  extreme: 'bg-red-500/20 text-red-500 border-red-500/30',
};

const Pill = ({ value, map }) => (
  <Badge variant="outline" className={`rounded-sm text-[10px] whitespace-nowrap ${map[value] || 'border-border'}`} data-testid={`badge-${value}`}>
    {(value || '').replace(/_/g, ' ').toUpperCase()}
  </Badge>
);

export default function MerchantsAdminPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [merchants, setMerchants] = useState([]);
  const [counts, setCounts] = useState({ total: 0, not_started: 0, pending_review: 0, approved: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ kyc_status: 'all', risk_category: 'all', account_status: 'all', q: '' });

  const [detail, setDetail] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showServices, setShowServices] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [showFreeze, setShowFreeze] = useState(false);
  const [selected, setSelected] = useState(null);
  const [svc, setSvc] = useState({});
  const [busy, setBusy] = useState(false);
  const [statusReason, setStatusReason] = useState('');

  const fetchMerchants = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.kyc_status !== 'all') params.append('kyc_status', filters.kyc_status);
      if (filters.risk_category !== 'all') params.append('risk_category', filters.risk_category);
      if (filters.account_status !== 'all') params.append('account_status', filters.account_status);
      if (filters.q) params.append('q', filters.q);
      const res = await api.get(`/admin/merchants?${params.toString()}`);
      setMerchants(res.data.merchants || []);
      if (res.data.counts) setCounts(res.data.counts);
    } catch (e) {
      toast.error('Failed to fetch merchants');
    } finally {
      setLoading(false);
    }
  }, [api, filters]);

  useEffect(() => {
    const t = setTimeout(fetchMerchants, 300);
    return () => clearTimeout(t);
  }, [fetchMerchants]);

  const openDetail = async (m) => {
    setShowDetail(true);
    setDetail(null);
    try {
      const res = await api.get(`/admin/merchants/${m.id}`);
      setDetail(res.data);
    } catch (e) {
      toast.error('Failed to load merchant detail');
    }
  };

  const openServices = (m) => {
    setSelected(m);
    setSvc({ ...m.services });
    setShowServices(true);
  };

  const saveServices = async (patch) => {
    setBusy(true);
    try {
      const params = new URLSearchParams(patch).toString();
      const res = await api.put(`/admin/merchants/${selected.id}/services?${params}`);
      setSvc(res.data.services);
      setMerchants((prev) => prev.map((x) => x.id === selected.id ? { ...x, services: res.data.services } : x));
      toast.success('Service access updated');
      fetchMerchants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update services');
    } finally {
      setBusy(false);
    }
  };

  const openStatus = (m) => {
    setSelected(m);
    setStatusReason('');
    setShowStatus(true);
  };

  const applyStatus = async (status) => {
    setBusy(true);
    try {
      await api.post(`/admin/merchants/${selected.id}/account-status`, { status, reason: statusReason });
      toast.success(`Merchant ${status}`);
      setShowStatus(false);
      fetchMerchants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    } finally {
      setBusy(false);
    }
  };

  const openFreeze = (m) => {
    setSelected(m);
    setShowFreeze(true);
  };

  const confirmFreeze = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/merchants/${selected.id}/freeze`, { freeze: !selected.is_payout_frozen, reason: 'Toggled from Merchant Management' });
      toast.success(selected.is_payout_frozen ? 'Payouts unfrozen' : 'Payouts frozen');
      setShowFreeze(false);
      fetchMerchants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to toggle freeze');
    } finally {
      setBusy(false);
    }
  };

  const ActionsMenu = ({ m }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 px-2 rounded-sm" data-testid={`actions-${m.mid}`}>
          <MoreVertical className="h-4 w-4" />
          <span className="ml-1 hidden sm:inline text-xs">Actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={() => openDetail(m)} data-testid={`view-${m.mid}`}>
          <Eye className="h-4 w-4 mr-2" />View Merchant
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/admin/kyc-review')} data-testid={`kyc-${m.mid}`}>
          <FileCheck className="h-4 w-4 mr-2" />KYC Review
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => openServices(m)} data-testid={`services-${m.mid}`}>
          <Settings2 className="h-4 w-4 mr-2" />Service Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => openFreeze(m)} data-testid={`freeze-${m.mid}`}>
          <Ban className="h-4 w-4 mr-2" />{m.is_payout_frozen ? 'Unfreeze Account' : 'Freeze Account'}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => openStatus(m)} className="text-red-500 focus:text-red-500" data-testid={`status-${m.mid}`}>
          <ShieldCheck className="h-4 w-4 mr-2" />{m.account_status === 'suspended' ? 'Activate Account' : 'Suspend Account'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="space-y-6" data-testid="merchant-management-page">
      <div>
        <h1 className="text-2xl font-heading font-bold">Merchant Management</h1>
        <p className="text-muted-foreground text-sm">Complete merchant directory — all accounts regardless of KYC status</p>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { key: 'total', label: 'Total Merchants', value: counts.total, Icon: Users, cls: 'text-primary', bg: 'bg-primary/10' },
          { key: 'not_started', label: 'Not Started', value: counts.not_started, Icon: UserPlus, cls: 'text-muted-foreground', bg: 'bg-muted' },
          { key: 'pending_review', label: 'Pending Review', value: counts.pending_review, Icon: Clock, cls: 'text-yellow-600', bg: 'bg-yellow-500/15' },
          { key: 'approved', label: 'Approved', value: counts.approved, Icon: CheckCircle2, cls: 'text-green-600', bg: 'bg-green-500/15' },
        ].map(({ key, label, value, Icon, cls, bg }) => (
          <Card key={key} data-testid={`count-${key}`}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${bg}`}>
                <Icon className={`h-5 w-5 ${cls}`} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
                {loading ? <Skeleton className="h-6 w-10 mt-1" /> : <p className="text-2xl font-bold font-heading leading-none mt-0.5" data-testid={`count-value-${key}`}>{value}</p>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[220px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by MID, name, email, PAN, phone..."
                  value={filters.q}
                  onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                  className="pl-9 rounded-sm"
                  data-testid="merchant-search"
                />
              </div>
            </div>
            <Select value={filters.kyc_status} onValueChange={(v) => setFilters({ ...filters, kyc_status: v })}>
              <SelectTrigger className="w-[160px] rounded-sm" data-testid="kyc-filter"><SelectValue placeholder="All KYC" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All KYC</SelectItem>
                <SelectItem value="not_started">Not Started</SelectItem>
                <SelectItem value="incomplete">Incomplete</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="reupload_required">Reupload Required</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.risk_category} onValueChange={(v) => setFilters({ ...filters, risk_category: v })}>
              <SelectTrigger className="w-[140px] rounded-sm" data-testid="risk-filter"><SelectValue placeholder="All Risk" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risk</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="extreme">Extreme</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.account_status} onValueChange={(v) => setFilters({ ...filters, account_status: v })}>
              <SelectTrigger className="w-[150px] rounded-sm" data-testid="status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Desktop table */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card z-10">
                <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="py-3 px-4">MID</th>
                  <th className="py-3 px-4">Merchant</th>
                  <th className="py-3 px-4">Entity Type</th>
                  <th className="py-3 px-4">Email</th>
                  <th className="py-3 px-4">KYC Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="border-b">{[...Array(6)].map((_, j) => <td key={j} className="py-3 px-4"><Skeleton className="h-4 w-full" /></td>)}</tr>
                  ))
                ) : merchants.length ? merchants.map((m) => (
                  <tr key={m.id} className="border-b last:border-0 hover:bg-muted/40 transition-colors" data-testid={`merchant-row-${m.mid}`}>
                    <td className="py-3 px-4 font-mono text-xs font-semibold text-primary whitespace-nowrap" data-testid={`mid-${m.mid}`}>{m.mid}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div>
                          <p className="font-medium leading-tight">{m.business_name}</p>
                          {m.merchant_name && m.merchant_name !== m.business_name && (
                            <p className="text-xs text-muted-foreground leading-tight">{m.merchant_name}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 capitalize text-xs">{(m.entity_type || '—').replace(/_/g, ' ')}</td>
                    <td className="py-3 px-4 text-xs">{m.email || '—'}</td>
                    <td className="py-3 px-4"><Pill value={m.kyc_status} map={KYC_BADGE} /></td>
                    <td className="py-3 px-4 text-right"><ActionsMenu m={m} /></td>
                  </tr>
                )) : (
                  <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No merchants found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Mobile card list */}
      <div className="md:hidden space-y-3">
        {loading ? (
          [...Array(4)].map((_, i) => <Card key={i}><CardContent className="p-4"><Skeleton className="h-16 w-full" /></CardContent></Card>)
        ) : merchants.length ? merchants.map((m) => (
          <Card key={m.id} data-testid={`merchant-card-${m.mid}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-mono text-xs font-semibold text-primary">{m.mid}</p>
                  <p className="font-medium truncate">{m.business_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{m.email || '—'}</p>
                  <p className="text-xs text-muted-foreground capitalize mt-0.5">{(m.entity_type || '—').replace(/_/g, ' ')}</p>
                </div>
                <ActionsMenu m={m} />
              </div>
              <div className="mt-3"><Pill value={m.kyc_status} map={KYC_BADGE} /></div>
            </CardContent>
          </Card>
        )) : (
          <Card><CardContent className="p-8 text-center text-muted-foreground">No merchants found</CardContent></Card>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-lg" data-testid="merchant-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.business_name || 'Merchant'} · <span className="font-mono text-primary text-sm">{detail?.mid}</span></DialogTitle>
            <DialogDescription>Merchant-specific overview</DialogDescription>
          </DialogHeader>
          {!detail ? <Skeleton className="h-40 w-full" /> : (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-muted-foreground">Email</p><p>{detail.email || '—'}</p></div>
                <div><p className="text-xs text-muted-foreground">Phone</p><p>{detail.phone || '—'}</p></div>
                <div><p className="text-xs text-muted-foreground">Entity</p><p className="capitalize">{(detail.entity_type || '—').replace(/_/g, ' ')}</p></div>
                <div><p className="text-xs text-muted-foreground">PAN</p><p className="font-mono">{detail.pan_number || '—'}</p></div>
                <div><p className="text-xs text-muted-foreground">Registered</p><p>{detail.created_at ? new Date(detail.created_at).toLocaleDateString('en-IN') : '—'}</p></div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Pill value={detail.kyc_status} map={KYC_BADGE} />
                <Pill value={detail.payout_status} map={SERVICE_BADGE} />
                <Pill value={detail.wallet_status} map={SERVICE_BADGE} />
                <Pill value={detail.account_status} map={SERVICE_BADGE} />
                <Pill value={detail.risk_category} map={RISK_BADGE} />
              </div>
              <div className="grid grid-cols-3 gap-3 pt-2 border-t">
                <div><p className="text-xs text-muted-foreground flex items-center gap-1"><WalletIcon className="h-3 w-3" />Available</p><p className="font-mono">₹{(detail.wallet?.available_balance || 0).toLocaleString('en-IN')}</p></div>
                <div><p className="text-xs text-muted-foreground">Payouts</p><p className="font-mono">{detail.counts?.payouts ?? 0}</p></div>
                <div><p className="text-xs text-muted-foreground">Beneficiaries</p><p className="font-mono">{detail.counts?.beneficiaries ?? 0}</p></div>
              </div>
              {detail.reseller && <p className="text-xs text-muted-foreground">Reseller: {detail.reseller.reseller_code} · {detail.reseller.business_name}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetail(false)} className="rounded-sm">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Services dialog */}
      <Dialog open={showServices} onOpenChange={setShowServices}>
        <DialogContent data-testid="services-dialog">
          <DialogHeader>
            <DialogTitle>Service Access · <span className="font-mono text-primary text-sm">{selected?.mid}</span></DialogTitle>
            <DialogDescription>Payout requires approved KYC. Payin is inactive unless enabled.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {[
              { key: 'wallet_funding_enabled', label: 'Wallet Funding', desc: 'Allow this merchant to add funds' },
              { key: 'payout_enabled', label: 'Payout', desc: selected?.kyc_status !== 'approved' ? 'Locked — KYC must be approved first' : 'Allow payout creation' },
              { key: 'payin_enabled', label: 'Payin', desc: 'Payment collection (inactive platform-wide)' },
              { key: 'api_enabled', label: 'API Access', desc: 'Programmatic API access' },
            ].map((row) => (
              <div key={row.key} className="flex items-center justify-between p-3 rounded-lg border">
                <div><p className="font-medium text-sm">{row.label}</p><p className="text-xs text-muted-foreground">{row.desc}</p></div>
                <Switch
                  checked={!!svc[row.key]}
                  disabled={busy || (row.key === 'payout_enabled' && selected?.kyc_status !== 'approved')}
                  onCheckedChange={(v) => saveServices({ [row.key]: v })}
                  data-testid={`svc-${row.key}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowServices(false)} className="rounded-sm">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Freeze confirmation dialog */}
      <Dialog open={showFreeze} onOpenChange={setShowFreeze}>
        <DialogContent data-testid="freeze-dialog">
          <DialogHeader>
            <DialogTitle>{selected?.is_payout_frozen ? 'Unfreeze' : 'Freeze'} Merchant Account · <span className="font-mono text-primary text-sm">{selected?.mid}</span></DialogTitle>
            <DialogDescription>
              {selected?.is_payout_frozen
                ? "This will restore the merchant's account according to the configured policy."
                : "This will temporarily restrict the merchant's account according to the configured freeze policy."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFreeze(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={confirmFreeze} disabled={busy} className="rounded-sm" data-testid="confirm-freeze-btn">
              {selected?.is_payout_frozen ? 'Confirm Unfreeze' : 'Confirm Freeze'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status dialog */}
      <Dialog open={showStatus} onOpenChange={setShowStatus}>
        <DialogContent data-testid="status-dialog">
          <DialogHeader>
            <DialogTitle>{selected?.account_status === 'suspended' ? 'Activate' : 'Suspend'} Merchant · <span className="font-mono text-primary text-sm">{selected?.mid}</span></DialogTitle>
            <DialogDescription>{selected?.account_status === 'suspended' ? 'Restore account access.' : 'A reason is required to suspend an account.'}</DialogDescription>
          </DialogHeader>
          {selected?.account_status !== 'suspended' && (
            <div className="space-y-2 py-2">
              <Label className="text-xs uppercase tracking-wider">Reason *</Label>
              <Textarea value={statusReason} onChange={(e) => setStatusReason(e.target.value)} placeholder="Reason for suspension..." className="rounded-sm" data-testid="status-reason" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatus(false)} className="rounded-sm">Cancel</Button>
            {selected?.account_status === 'suspended' ? (
              <Button onClick={() => applyStatus('active')} disabled={busy} className="rounded-sm" data-testid="confirm-activate-btn">Activate</Button>
            ) : (
              <Button variant="destructive" onClick={() => applyStatus('suspended')} disabled={busy} className="rounded-sm" data-testid="confirm-suspend-btn">Suspend</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
